import { useAiToolkitContext } from '@app/(darkHeader)/ai/_shared/_context/AiToolkitContext';
import { type JSX, useCallback } from 'react';

import { ConfirmationModal, Text } from '@videoblocks/design-system';

import { pluralize } from '../../../../../../utils/utils';
import { getCreditCost } from '../../_context/getCreditCost';
import { AiToolkitCategory, AI_TOOLKIT_CATEGORIES } from '../../_context/types';

type RegenerateConfirmationModalProps = {
    onConfirm: () => void | Promise<void>;
};

const getModalTitle = (category: AiToolkitCategory, calculatedCost: number): string => {
    switch (category) {
        case AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION:
            return `Start a new generation for ${calculatedCost} ${pluralize(calculatedCost, 'credit')}`;
        case AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION:
            return `Start a new generation for ${calculatedCost} ${pluralize(calculatedCost, 'credit')}`;
        case AI_TOOLKIT_CATEGORIES.VOICEOVER:
            return '';
    }
};

const getModalContent = (
    category: AiToolkitCategory,
    calculatedCost: number,
    credits: number,
): JSX.Element | string => {
    switch (category) {
        case AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION:
            return (
                <div className="flex flex-col gap-6">
                    <Text fontWeight="regular">
                        Your current preview will be replaced once the new one is ready. Download it now or access your
                        emailed copy anytime.
                    </Text>
                    <Text fontWeight="regular">
                        You&apos;ll have {credits - calculatedCost} {pluralize(credits - calculatedCost, 'credit')}
                        &nbsp;remaining.
                    </Text>
                </div>
            );
        case AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION:
            return (
                <div className="flex flex-col gap-6">
                    <Text fontWeight="regular">
                        Your current image will be replaced once the new one is ready. Download it now or access your
                        emailed copy anytime.
                    </Text>
                    <Text fontWeight="regular">
                        You&apos;ll have {credits - calculatedCost} {pluralize(credits - calculatedCost, 'credit')}
                        &nbsp;remaining.
                    </Text>
                </div>
            );
        case AI_TOOLKIT_CATEGORIES.VOICEOVER:
            return '';
    }
};

export const RegenerateConfirmationModal = ({ onConfirm }: RegenerateConfirmationModalProps): JSX.Element => {
    const { confirmationModal, formStates, credits, dispatchConfirmationModal } = useAiToolkitContext();

    const { type, category = AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION } = confirmationModal ?? {};
    const isOpen = type === 'regenerate' && confirmationModal?.isOpen === true;
    const calculatedCost = getCreditCost(category, formStates);

    const onCancel = useCallback((): void => {
        dispatchConfirmationModal({ type: 'CLOSE_MODAL' });
    }, [dispatchConfirmationModal]);

    const handleConfirm = useCallback((): void => {
        void onConfirm();
    }, [onConfirm]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            onCancel={onCancel}
            onConfirm={handleConfirm}
            title={getModalTitle(category, calculatedCost)}
            confirmLabel="Generate New"
            content={getModalContent(category, calculatedCost, credits)}
        />
    );
};
